import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Box,
  Button,
  Chip,
  Stack,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Grid,
  Paper,
  Badge
} from '@mui/material';
import {
  ArrowBack,
  Description,
  VerifiedUser as Users,
  CalendarTodayRounded as Calendar,
  Camera,
  Edit,
  ContentCopy,
  ArrowForward
} from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import { Link, useNavigate } from 'react-router-dom';
import axiosinstance from '../../utils/axiosConfig';
import TechnicalPhase from './TechnicalPhase';
import MarketingPhase from './MarketingPhase';
import MarketingSchedule from './MarketingSchedule';
import InvitesPhase from './InvitesPhase';
import CampaignTrackingPhase from './CampaignTrackingPhase';
import ResourceOverview from './ResourceOverview'; // NEW IMPORT

// Debounce utility
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  }) as T;
}

interface ResourceDetailProps {
  performanceId?: string;
}

const ResourceDetail: React.FC<ResourceDetailProps> = ({ performanceId }) => {
  const [resource, setResource] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState(0);
  const [saving, setSaving] = useState(false);
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const phases = [
    { id: 'basic', label: 'Basic Info', icon: Description },
    { id: 'technical', label: 'Technical', icon: SettingsIcon },
    { id: 'marketing', label: 'Marketing', icon: Camera },
    { id: 'marketing_schedule', label: 'Marketing Schedule', icon: Calendar },
    { id: 'invites', label: 'Invites', icon: Users },
    { id: 'campaign', label: 'Campaign Track', icon: Calendar }
  ];

  // Check if this is a meaningful resource or just empty shell
  const isEmptyResource = useCallback((res: any) => {
    if (!res) return true;
    
    // If resource has been saved/updated after creation, show overview
    if (res.updatedAt && res.createdAt && new Date(res.updatedAt) > new Date(res.createdAt)) {
      return false;
    }
    
    // If no longer a draft, someone has worked on it
    if (res.isDraft === false) {
      return false;
    }
    
    // Only show setup for truly fresh draft resources
    return res.isDraft === true;
  }, []);

  // Get phase content summary for quick overview
  const getPhaseContentSummary = (phaseId: string) => {
    if (!resource) return { count: 0, items: [], actionText: 'Add content' };
    
    switch (phaseId) {
      case 'technical':
        const techItems = [
          resource.schedulePdfUrl && 'Schedule PDF',
          resource.signedContractUrl && 'Signed Contract',
          resource.techSpecs && 'Tech Specs',
          resource.setupNotes && 'Setup Notes'
        ].filter(Boolean);
        return {
          count: techItems.length,
          items: techItems,
          actionText: techItems.length === 0 ? 'Upload documents' : 'View documents'
        };
        
      case 'marketing':
        const marketingItems = [
          resource.marketingImages?.length > 0 && `${resource.marketingImages.length} images`,
          resource.pressRelease && 'Press release',
          resource.marketingSchedule?.length > 0 && 'Marketing schedule',
          resource.socialMediaContent?.length > 0 && 'Social content'
        ].filter(Boolean);
        return {
          count: marketingItems.length,
          items: marketingItems,
          actionText: marketingItems.length === 0 ? 'Add marketing materials' : 'View marketing'
        };

      case 'marketing_schedule':
        const scheduleItems = resource.marketingSchedule || [];
        const completed = scheduleItems.filter((item: any) => item.status === 'completed').length;
        return {
          count: scheduleItems.length,
          items: scheduleItems.length > 0 ? [`${scheduleItems.length} tasks`, `${completed} completed`] : [],
          actionText: scheduleItems.length === 0 ? 'Create schedule' : 'Manage schedule'
        };
        
      case 'invites':
        const inviteItems = [
          resource.targetedInvites?.length > 0 && `${resource.targetedInvites.length} invites`,
          resource.inviteEmailTemplate && 'Email template'
        ].filter(Boolean);
        return {
          count: inviteItems.length,
          items: inviteItems,
          actionText: inviteItems.length === 0 ? 'Set up invites' : 'Manage invites'
        };
        
      case 'campaign':
        const campaignItems = [
          resource.publicityTimeline?.length > 0 && `${resource.publicityTimeline.length} timeline items`
        ].filter(Boolean);
        return {
          count: campaignItems.length,
          items: campaignItems,
          actionText: campaignItems.length === 0 ? 'Start tracking' : 'View campaign'
        };
        
      default:
        return { count: 0, items: [], actionText: 'Add content' };
    }
  };

  // Smart auto-save with debounce
  const handleSmartResourceUpdate = useCallback(
    debounce(async (updatedFields: any) => {
      try {
        setSaving(true);
        
        const updatedResource = { ...resource, ...updatedFields };
        
        if (resource._id) {
          const response = await axiosinstance.put(`/api/resources/${resource._id}`, updatedFields);
          setResource(response.data);
        } else {
          const response = await axiosinstance.post('/api/resources', {
            ...resource,
            ...updatedFields,
            isDraft: false
          });
          setResource(response.data);
        }
        
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        setSaving(false);
      }
    }, 1000),
    [resource]
  );

  const handleResourceUpdate = (updatedResource: any) => {
    setResource(updatedResource);
  };

  const handleFieldChange = (field: string, value: any) => {
    const updatedFields = { [field]: value };
    setResource((prev: any) => ({ ...prev, ...updatedFields }));
    handleSmartResourceUpdate(updatedFields);
  };

useEffect(() => {
  const abortController = new AbortController();
  
  const fetchOrCreateResource = async () => {
    // Prevent multiple simultaneous creation attempts
    if (isCreating) return;
    
    try {
      setLoading(true);
      
      let response = await axiosinstance.get(`/api/resources/performance/${performanceId}`, {
        signal: abortController.signal
      });
      let resourceData = response.data;
      
      if (!resourceData.exists) {
        console.log('No resource exists, creating automatically...');
        
        // Set creating flag to prevent duplicates
        setIsCreating(true);
        
        try {
          const createResponse = await axiosinstance.post('/api/resources', {
            name: resourceData.name,
            resourceType: resourceData.resourceType,
            resourcePhase: resourceData.resourcePhase,
            visibility: resourceData.visibility,
            performances: resourceData.performances,
            bandRecommendation: resourceData.bandRecommendation,
            strandId: resourceData.strandId,
            programmingId: resourceData.programmingId,
            isDraft: true
          }, {
            signal: abortController.signal
          });
          
          resourceData = createResponse.data;
          console.log('✅ Resource created successfully:', resourceData._id);
          
        } catch (createError: any) {
          if (createError.response?.status === 409) {
            console.log('Resource already exists, fetching existing one...');
            resourceData = createError.response.data.resource;
          } else {
            throw createError;
          }
        } finally {
          setIsCreating(false);
        }
      }
      
      // Determine if this is first time setup or repeat visit
      const isEmpty = isEmptyResource(resourceData);
      setIsFirstTimeSetup(isEmpty);
      setResource(resourceData);
      
      // Smart tab selection for repeat visits
      if (!isEmpty) {
        // Auto-focus on the phase with most relevant content
        if (resourceData.resourceType === 'marketing' && resourceData.marketingImages?.length > 0) {
          setActivePhase(2); // Marketing tab
        } else if (resourceData.resourceType === 'technical' && resourceData.schedulePdfUrl) {
          setActivePhase(1); // Technical tab
        } else if (resourceData.resourceType === 'invites' && resourceData.targetedInvites?.length > 0) {
          setActivePhase(4); // Invites tab
        }
      }
      
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error with resource:', error);
        setResource(null);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  if (performanceId) {
    fetchOrCreateResource();
  }

  // Cleanup function to abort requests if component unmounts
  return () => {
    abortController.abort();
  };
}, [performanceId]); // REMOVED isEmptyResource from dependencies

if (loading) {
  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {isCreating ? 'Creating resource...' : 'Loading resource...'}
        </Typography>
      </Box>
    </Container>
  );
}

  if (!resource) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          Unable to load resource data for this performance.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ my: 2 }}>
        <MuiLink component={Link} to="/" color="inherit">
          Home
        </MuiLink>
        <MuiLink component={Link} to="/admin/performances" color="inherit">
          Performances
        </MuiLink>
        <Typography color="text.primary">Resources</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
            size={isMobile ? 'medium' : 'large'}
          >
            <ArrowBack />
          </IconButton>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            sx={{ flexGrow: 1 }}
          >
            {resource.name}
          </Typography>
        </Box>

        {/* Status - Only show meaningful info, no completion percentage */}
        <Stack 
          direction={isMobile ? "column" : "row"} 
          spacing={1} 
          sx={{ mb: 2 }}
          alignItems={isMobile ? "flex-start" : "center"}
        >
          <Chip 
            label={resource.resourceType === 'marketing' ? 'Marketing Focus' : 
                   resource.resourceType === 'technical' ? 'Technical Focus' : 
                   resource.resourceType === 'invites' ? 'Invites Focus' :
                   'Complete Resource'}
            color="primary" 
            size="small"
          />
          <Chip 
            label={resource.resourcePhase === 'pre_event' ? 'Pre-Event' :
                   resource.resourcePhase === 'live_event' ? 'Live Event' :
                   'Post-Event'}
            variant="outlined" 
            size="small"
          />
          {saving && (
            <Chip 
              label="Saving..." 
              color="info" 
              size="small"
              icon={<CircularProgress size={16} />}
            />
          )}
        </Stack>

        {/* Actions */}
        <Stack 
          direction={isMobile ? "column" : "row"} 
          spacing={1}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            size={isMobile ? "large" : "medium"}
            fullWidth={isMobile}
          >
            Clone for New Event
          </Button>
        </Stack>
      </Box>

      {/* FIRST TIME SETUP: Simple minimal form */}
      {isFirstTimeSetup ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Setup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Just need a couple details to get started, then you can jump straight into adding content.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Resource Name"
                  value={resource.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  variant="outlined"
                  placeholder="e.g., Villagers Spring Tour 2025"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Focus Area</InputLabel>
                  <Select
                    value={resource.resourceType || ''}
                    label="Focus Area"
                    onChange={(e) => handleFieldChange('resourceType', e.target.value)}
                  >
                    <MenuItem value="full">Complete Resources</MenuItem>
                    <MenuItem value="technical">Technical Focus</MenuItem>
                    <MenuItem value="marketing">Marketing Focus</MenuItem>
                    <MenuItem value="invites">Invites Focus</MenuItem>
                    <MenuItem value="contracting">Contracting Focus</MenuItem>
                    <MenuItem value="archive">Archive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {resource.name && resource.resourceType && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setIsFirstTimeSetup(false);
                    // Jump to relevant phase based on resource type
                    if (resource.resourceType === 'marketing') setActivePhase(2);
                    else if (resource.resourceType === 'technical') setActivePhase(1);
                    else if (resource.resourceType === 'invites') setActivePhase(4);
                    else setActivePhase(1);
                  }}
                  endIcon={<ArrowForward />}
                >
                  Start Adding Content
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        /* REPEAT VISITS: Show ResourceOverview instead of quick cards */
        <ResourceOverview 
          resource={resource} 
          onPhaseJump={setActivePhase}
          onUpdate={handleResourceUpdate}
        />
      )}

      {/* Phase Navigation - Always show for non-first-time users */}
      {!isFirstTimeSetup && (
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={activePhase}
            onChange={(_, newValue) => setActivePhase(newValue)}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
          >
            {phases.map((phase, index) => {
              const Icon = phase.icon;
              const summary = getPhaseContentSummary(phase.id);
              
              return (
                <Tab
                  key={phase.id}
                  icon={
                    <Badge 
                      badgeContent={summary.count > 0 ? summary.count : null} 
                      color="primary"
                    >
                      <Icon />
                    </Badge>
                  }
                  label={phase.label}
                  iconPosition="top"
                  sx={{ textTransform: 'none' }}
                />
              );
            })}
          </Tabs>
        </Card>
      )}

      {/* Phase Content */}
      {!isFirstTimeSetup && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            {/* Basic Info Phase - Simplified for repeat visits */}
            {activePhase === 0 && (
              <Box sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Resource Name"
                      value={resource.name || ''}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Resource Type</InputLabel>
                      <Select
                        value={resource.resourceType || ''}
                        label="Resource Type"
                        onChange={(e) => handleFieldChange('resourceType', e.target.value)}
                      >
                        <MenuItem value="full">Complete Resources</MenuItem>
                        <MenuItem value="technical">Technical Focus</MenuItem>
                        <MenuItem value="marketing">Marketing Focus</MenuItem>
                        <MenuItem value="invites">Invites Focus</MenuItem>
                        <MenuItem value="contracting">Contracting Focus</MenuItem>
                        <MenuItem value="archive">Archive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Event Phase</InputLabel>
                      <Select
                        value={resource.resourcePhase || 'pre_event'}
                        label="Event Phase"
                        onChange={(e) => handleFieldChange('resourcePhase', e.target.value)}
                      >
                        <MenuItem value="pre_event">Pre-Event Planning</MenuItem>
                        <MenuItem value="live_event">Live Event</MenuItem>
                        <MenuItem value="post_event">Post-Event</MenuItem>
                        <MenuItem value="mixed">Mixed Phase</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Visibility</InputLabel>
                      <Select
                        value={resource.visibility || 'internal'}
                        label="Visibility"
                        onChange={(e) => handleFieldChange('visibility', e.target.value)}
                      >
                        <MenuItem value="internal">Internal Use</MenuItem>
                        <MenuItem value="public">Public Access</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      variant="outlined"
                      value={resource.notes || ''}
                      onChange={(e) => handleFieldChange('notes', e.target.value)}
                      placeholder="Add any general notes about this resource pack..."
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Other Phases */}
            {activePhase === 1 && <TechnicalPhase resource={resource} onUpdate={handleResourceUpdate} />}
            {activePhase === 2 && <MarketingPhase resource={resource} onUpdate={handleResourceUpdate} />}
            {activePhase === 3 && <MarketingSchedule resource={resource} onUpdate={handleResourceUpdate} />}
            {activePhase === 4 && <InvitesPhase resource={resource} onUpdate={handleResourceUpdate} />}
            {activePhase === 5 && <CampaignTrackingPhase resource={resource} onUpdate={handleResourceUpdate} />}
          </CardContent>
        </Card>
      )}

      {/* Mobile save button */}
      {isMobile && (
        <Box sx={{ position: 'sticky', bottom: 0, py: 2, backgroundColor: 'background.paper' }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={saving ? <CircularProgress size={20} /> : <Edit />}
            disabled={saving}
          >
            {saving ? 'Auto-saving...' : 'All changes saved automatically'}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default ResourceDetail;